import React, { useEffect, useRef, useCallback } from 'react';
import { useProctor } from '../../context/ProctorContext';
import { DetectionOverlay } from './DetectionOverlay';
import { ZONE_META, type GazeZone } from '../../engine/GazeZoneClassifier';
import type { Detection } from '../../hooks/useObjectDetector';

interface LeftPanelProps {
  onKeyDown: (e: KeyboardEvent) => void;
  stylusHandlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
  };
  roll?: number;
  fps?: number;
  hasFace?: boolean;
  eyeTrackerStatus?: string;
  gazeZone?: GazeZone;
  detections?: Detection[];
}

export function LeftPanel({ onKeyDown, stylusHandlers, roll = 0, fps = 0, hasFace = false, eyeTrackerStatus = '', gazeZone = 'forward', detections = [] }: LeftPanelProps) {
  const { gazeData, phase } = useProctor();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const poseCanvasRef = useRef<HTMLCanvasElement>(null);

  // Attach keyboard listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => onKeyDown(e);
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onKeyDown]);

  // Resize draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.parentElement?.getBoundingClientRect();
    if (rect) {
      canvas.width = Math.floor(rect.width - 24);
      canvas.height = Math.max(60, Math.floor(rect.height - 30));
    }
  }, [phase]);

  const yaw = gazeData?.yaw ?? 0;
  const pitch = gazeData?.pitch ?? 0;
  const hv = gazeData?.headVector ?? [0, 0, 0];

  // Draw pose indicator radar
  useEffect(() => {
    const c = poseCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;

    const w = 100, h = 100, cx = w / 2, cy = h / 2, r = 36;
    ctx.clearRect(0, 0, w, h);

    // Background circle
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = '#2a2d3a';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Crosshair
    ctx.beginPath();
    ctx.moveTo(cx - r, cy); ctx.lineTo(cx + r, cy);
    ctx.moveTo(cx, cy - r); ctx.lineTo(cx, cy + r);
    ctx.strokeStyle = '#2a2d3a';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Threshold circles (inner ring at 8°, outer at 15°)
    [8, 15].forEach(deg => {
      const tr = (deg / 45) * r;
      ctx.beginPath();
      ctx.arc(cx, cy, tr, 0, Math.PI * 2);
      ctx.strokeStyle = deg === 8 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)';
      ctx.setLineDash([2, 2]);
      ctx.stroke();
      ctx.setLineDash([]);
    });

    // Dot position (clamped to circle)
    const scale = r / 45;
    let dotX = cx + yaw * scale;
    let dotY = cy - pitch * scale;
    const dx = dotX - cx, dy = dotY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > r) {
      dotX = cx + (dx / dist) * r;
      dotY = cy + (dy / dist) * r;
    }

    // Dot color based on zone
    const zoneMeta = ZONE_META[gazeZone];
    const col = zoneMeta.color;

    // Draw line from center to dot
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(dotX, dotY);
    ctx.strokeStyle = col + '60';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw dot
    ctx.beginPath();
    ctx.arc(dotX, dotY, 5, 0, Math.PI * 2);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [yaw, pitch, gazeZone]);

  return (
    <div className="left-panel">
      {/* VideoFeed */}
      <div className="video-wrap">
        <video id="webcamVideo" autoPlay playsInline muted />
        <canvas id="overlayCanvas" />
        <canvas id="overlayCanvasHand" />
        {phase === 'monitoring' && detections.length > 0 && (
          <DetectionOverlay detections={detections} />
        )}
      </div>

      {/* Angle Cards */}
      <div className="angles">
        <div className="angle-card">
          <div className="label">Yaw (좌우)</div>
          <div className="val">{yaw.toFixed(1)}°</div>
        </div>
        <div className="angle-card">
          <div className="label">Pitch (상하)</div>
          <div className="val">{pitch.toFixed(1)}°</div>
        </div>
        <div className="angle-card">
          <div className="label">Roll (기울기)</div>
          <div className="val">{roll.toFixed(1)}°</div>
        </div>
      </div>

      {/* Pose Indicator */}
      <div className="pose-row">
        <canvas ref={poseCanvasRef} id="poseCanvas" width={100} height={100} />
        <div className="pose-info">
          <div className="dir" style={{
            color: Math.sqrt(yaw * yaw + pitch * pitch) > 15 ? 'var(--red)' :
                   Math.sqrt(yaw * yaw + pitch * pitch) > 8 ? 'var(--yellow)' : 'var(--green)',
          }}>
            {getDirectionLabel(yaw, pitch)}
          </div>
          <div>[{hv[0].toFixed(3)}, {hv[1].toFixed(3)}, {hv[2].toFixed(3)}]</div>
          <div>FPS: {fps} {hasFace ? '얼굴 감지' : '얼굴 없음'}</div>
          <div style={{ fontSize: 9, color: eyeTrackerStatus === 'BlazeGaze' ? 'var(--green)' : 'var(--yellow)' }}>
            Eye: {eyeTrackerStatus}
          </div>
        </div>
      </div>

      {/* Calibration Bar */}
      {phase === 'calibrating' && (
        <div className="calib-bar">
          <div className="calib-label">캘리브레이션 진행 중</div>
          <div className="bar"><div className="bar-fill" style={{ width: '0%' }} /></div>
        </div>
      )}

      {/* Input Section */}
      {phase === 'monitoring' && (
        <div className="input-section">
          <div className="input-box">
            <div className="input-title">
              <span>답안 작성 (키보드)</span>
            </div>
            <textarea
              ref={textareaRef}
              placeholder="여기에 답안을 작성하세요..."
            />
          </div>
          <div className="input-box">
            <div className="input-title">
              <span>풀이 메모 (스타일러스)</span>
            </div>
            <canvas
              ref={canvasRef}
              id="drawCanvas"
              onPointerDown={stylusHandlers.onPointerDown}
              onPointerMove={stylusHandlers.onPointerMove}
              onPointerUp={stylusHandlers.onPointerUp}
              onPointerLeave={stylusHandlers.onPointerUp}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function getDirectionLabel(yaw: number, pitch: number): string {
  const absYaw = Math.abs(yaw);
  const dist = Math.sqrt(yaw * yaw + pitch * pitch);
  if (dist < 8) return '정면 집중';
  if (pitch > 10) return '위 (생각 중)';
  if (pitch < -8) return '아래';
  if (absYaw > 12) return yaw > 0 ? '오른쪽' : '왼쪽';
  if (dist > 20) return '먼 곳';
  return '정면 근처';
}
